/**
 * Шапка Рафика: упрощённая навигация.
 *
 * Навигация: витрина · ленты · студия · события · статистика
 * Справа: только аватарка + имя
 */

import { Who } from '../access/Who';
import { useSession } from '../access/session';
import { FEED7, POLYQUANT, EXPERIMENTS, PRIVATE_MIRROR, FEED3, RU_FEED, TRANSFERS } from '../lib/feedContext';
import { feedHref, switchFeed, useFeedNav } from './feedSwitch';
import { LAB_BUNDLE, learnHref, appHref, studioHref, eventsHref, isEvents, reportHref, liveHref, isLenta5, isAllFeed, pageLine } from './route';
import { type Mode, useScreen } from './store';
import { parked } from './parked';
import { ExtWarn } from './ExtWarn';
import { PhoneHead } from './phone/PhoneHead';
import { usePhone } from './phone/usePhone';
import './pilotHead.css';

export type FeedId = 'all' | 'live' | 'prematch' | 'lenta2' | 'lenta3' | 'lenta5' | 'lenta6' | 'lenta7' | 'russkie' | 'russkie-prematch' | 'russkie-live' | 'transfers';

const FEED_LABELS: Record<FeedId, string> = {
  all: 'Все',
  prematch: 'прематч',
  live: 'лайв',
  lenta2: 'лента 2.0',
  lenta3: 'лента 3.0',
  lenta5: 'лента 5.0',
  lenta6: 'лента 6.0',
  lenta7: 'лента 7.0',
  russkie: 'русские лайв',
  'russkie-prematch': 'русские прематч',
  'russkie-live': 'русские лайв',
  transfers: 'переливные',
};

/** Порядок табов на полосе */
export function availableFeeds(): FeedId[] {
  const feeds: FeedId[] = ['prematch', 'live'];
  if (PRIVATE_MIRROR) return feeds;
  feeds.unshift('all');
  if (!parked.lenta2) feeds.push('lenta2');
  feeds.push('lenta7');
  if (!parked.lenta5) feeds.push('lenta5');
  if (!parked.russkie) feeds.push('russkie-prematch', 'russkie-live');
  if (!parked.transfers) feeds.push('transfers');
  return feeds;
}

/** Какая лента открыта сейчас. Источник живой: его меняет вкладка без перезагрузки. */
export function currentFeedId(): FeedId | null {
  if (PRIVATE_MIRROR) return pageLine(location.pathname) === 'prematch' ? 'prematch' : 'live';
  if (FEED7) return 'lenta7';
  if (POLYQUANT) return 'lenta6';
  if (TRANSFERS) return 'transfers';
  if (RU_FEED) return pageLine(location.pathname) === 'prematch' ? 'russkie-prematch' : 'russkie-live';
  if (FEED3) return 'lenta3';
  if (EXPERIMENTS) return 'lenta2';
  if (isAllFeed(location.pathname)) return 'all';
  if (isLenta5(location.pathname, location.search)) return 'lenta5';
  const line = pageLine(location.pathname);
  if (line === 'prematch') return 'prematch';
  if (line === 'live') return 'live';
  return null;
}

/* ── Полоса лент (живёт внутри nx-pro > nx-wide, на ширине ленты) ── */

export function PilotFeedBar(): React.ReactElement {
  useFeedNav((s) => s.n);
  const current = currentFeedId();
  const feeds = availableFeeds();
  return (
    <div className="nx-pilot-feeds" style={{ '--feed-count': feeds.length } as React.CSSProperties}>
      {feeds.map((id) => (
        <a
          key={id}
          href={feedHref(id)}
          className={`nx-pilot-tab ${current === id ? 'nx-pilot-tab--on' : ''}`}
          aria-current={current === id ? 'page' : undefined}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            switchFeed(id);
          }}
        >
          {FEED_LABELS[id]}
        </a>
      ))}
    </div>
  );
}

/* ── Шапка ── */

export function PilotHead({
  modes,
  where = 'app',
  beat,
}: {
  modes: readonly Mode[];
  where?: 'app' | 'mid' | 'transfers' | 'ru' | 'predict' | 'report' | 'dry' | 'studio' | 'events' | 'learn' | 'cash' | 'forecast';
  beat?: { text: string; mood: 'ok' | 'still' | 'warn' };
}): React.ReactElement {
  const phone = usePhone();
  const mode = useScreen((s) => s.mode);
  const shelf = useScreen((s) => s.shelf);
  const setMode = useScreen((s) => s.setMode);
  const setShelf = useScreen((s) => s.setShelf);
  const userId = useSession((s) => s.me.id);
  const journal = Boolean(userId);

  const goWork = (id: Mode): void => {
    setMode(id);
    setShelf('work');
  };

  if (phone) return <PhoneHead modes={modes} where={where} beat={beat} />;

  return (
    <>
      <header className="nx-top nx-pilot-top">
        <div className="nx-wide nx-top__in">
          <div className="nx-logo">FORQ{LAB_BUNDLE ? ' lab' : ''}</div>

          <nav className="nx-seg nx-pilot-nav">
            {modes.includes('showcase') && (
              where === 'app' ? (
                <button type="button" aria-pressed={shelf === 'work' && mode === 'showcase'} onClick={() => goWork('showcase')}>
                  витрина
                </button>
              ) : (
                <a href={appHref()}>витрина</a>
              )
            )}

            {modes.includes('feed') && (
              where === 'app' ? (
                <button type="button" aria-pressed={shelf === 'work' && mode === 'feed'} onClick={() => goWork('feed')}>
                  ленты
                </button>
              ) : (
                <a href={liveHref()}>ленты</a>
              )
            )}

            {!PRIVATE_MIRROR && (
              <a href={learnHref()} aria-current={where === 'learn' ? 'page' : undefined}>
                обучение
              </a>
            )}

            <a href={studioHref()} aria-current={where === 'studio' ? 'page' : undefined}>
              студия
            </a>

            {!PRIVATE_MIRROR && (
              <a href={eventsHref()} aria-current={isEvents(location.pathname) ? 'page' : undefined}>
                события
              </a>
            )}

            {journal && !PRIVATE_MIRROR && (
              <a href={reportHref()} aria-current={where === 'report' ? 'page' : undefined}>
                статистика
              </a>
            )}
          </nav>

          <div className="nx-top__gap" />
          <Who />
        </div>
      </header>

      <div className="nx-wide nx-ext-region"><ExtWarn /></div>
    </>
  );
}
